import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Paper, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, } from '@mui/material';
import { fK, fX } from '../utils/formatters';
const KEYWORDS = [
    'Static',
    'Video',
    'UGC',
    'Retarget',
    'Prospecting',
    'Story',
    'Carousel',
    'DPA',
    'BAU',
    'Sale',
    'Collection',
    'Brand',
    'Dynamic',
    'Reels',
    'Image',
];
const LearningInsightsTab = ({ report }) => {
    const ads = report.ads || [];
    const la = useMemo(() => ads.filter((a) => a.result === 'Learning'), [ads]);
    if (la.length === 0) {
        return (_jsx(Box, { sx: { p: 6, textAlign: 'center', color: '#667085', fontSize: 14 }, children: "No Learning ads in this period." }));
    }
    // ── Same computation as standalone ─────────────────────────────────
    const withRoas = useMemo(() => la.map((a) => ({
        ...a,
        computedROAS: (a.atSpend || 0) > 0
            ? (a.atRevenue || 0) / a.atSpend
            : null,
        computedCTROAS: (a.atSpend || 0) > 0
            ? (a.atCtRevenue || 0) / a.atSpend
            : null,
        computedVTROAS: (a.atSpend || 0) > 0
            ? (a.atVtRevenue || 0) / a.atSpend
            : null,
        totalRev: a.atRevenue || 0,
        totalSpendL: a.atSpend || 0,
    })), [la]);
    const notableAds = useMemo(() => withRoas.filter((a) => a.computedROAS != null && a.computedROAS >= 1), [withRoas]);
    const lowROAS = useMemo(() => withRoas.filter((a) => a.computedROAS != null && a.computedROAS > 0 && a.computedROAS < 1), [withRoas]);
    const zeroROAS = useMemo(() => withRoas.filter((a) => a.computedROAS == null || a.computedROAS === 0), [withRoas]);
    const totalSpendAll = la.reduce((s, a) => s + (a.atSpend || 0), 0);
    const totalRevAll = la.reduce((s, a) => s + (a.atRevenue || 0), 0);
    const overallROAS = totalSpendAll > 0 ? totalRevAll / totalSpendAll : null;
    // Top 10 notable sorted desc by ROAS
    const top10Notable = useMemo(() => [...notableAds]
        .sort((a, b) => (b.computedROAS || 0) - (a.computedROAS || 0))
        .slice(0, 10), [notableAds]);
    // Keyword frequency — among profitable (notable) ads only
    const kwC = useMemo(() => {
        return KEYWORDS.map((kw) => {
            const matches = notableAds.filter((a) => (a.ad_name || '').toLowerCase().includes(kw.toLowerCase()));
            return {
                kw,
                count: matches.length,
                rev: matches.reduce((s, a) => s + (a.atRevenue || 0), 0),
            };
        })
            .filter((k) => k.count > 0)
            .sort((a, b) => b.count - a.count);
    }, [notableAds]);
    const totalLearning = la.length;
    // ── Render ─────────────────────────────────────────────────────────
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsxs(Grid, { container: true, spacing: 1.5, sx: { mb: 3 }, children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(HeroKPI, { label: "Learning Ads", value: String(totalLearning), sub: `${ads.length ? Math.round((totalLearning / ads.length) * 100) : 0}% of all ads`, leftColor: "#5A5A5A", valueColor: "#101828" }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(HeroKPI, { label: "Profitable (ROAS \u2265 1x)", value: String(notableAds.length), sub: `${totalLearning
                                ? Math.round((notableAds.length / totalLearning) * 100)
                                : 0}% of learning ads — stopped too early`, leftColor: "#027A48", valueColor: "#027A48" }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(HeroKPI, { label: "Total Spend (Learning)", value: fK(totalSpendAll), sub: `before hitting $${report.threshold} threshold`, leftColor: "#EE2A7B", valueColor: "#101828" }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsx(HeroKPI, { label: "Overall ROAS", value: overallROAS != null ? fX(overallROAS) : '—', sub: `${fK(totalRevAll)} revenue generated`, leftColor: "#EE2A7B", valueColor: "#101828" }) })] }), _jsxs(Grid, { container: true, spacing: 2.5, sx: { mb: 2.5 }, children: [_jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                border: '1px solid #EAECF0',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                bgcolor: '#fff',
                            }, children: [_jsxs(Box, { sx: {
                                        bgcolor: '#FAFAFA',
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid #EAECF0',
                                    }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "ROAS Performance Breakdown" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "How are learning ads performing before cutoff?" })] }), _jsx(Box, { sx: { p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }, children: [
                                        {
                                            label: 'Profitable — ROAS ≥ 1x (stopped too early)',
                                            value: notableAds.length,
                                            color: '#027A48',
                                        },
                                        {
                                            label: 'Generating revenue — ROAS < 1x',
                                            value: lowROAS.length,
                                            color: '#B08135',
                                        },
                                        {
                                            label: 'No revenue yet',
                                            value: zeroROAS.length,
                                            color: '#5A5A5A',
                                        },
                                    ].map((x) => (_jsxs(Box, { children: [_jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 0.5,
                                                }, children: [_jsxs(Box, { sx: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }, children: [_jsx(Box, { sx: {
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: '3px',
                                                                    bgcolor: x.color,
                                                                    flexShrink: 0,
                                                                } }), _jsx(Typography, { sx: {
                                                                    fontSize: 12,
                                                                    color: '#101828',
                                                                    fontWeight: 500,
                                                                }, children: x.label })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: {
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    color: x.color,
                                                                }, children: x.value }), _jsxs(Typography, { sx: {
                                                                    fontSize: 11,
                                                                    color: '#667085',
                                                                    minWidth: 38,
                                                                    textAlign: 'right',
                                                                }, children: [totalLearning
                                                                        ? Math.round((x.value / totalLearning) * 100)
                                                                        : 0, "%"] })] })] }), _jsx(Box, { sx: {
                                                    bgcolor: '#F2F4F7',
                                                    borderRadius: '4px',
                                                    height: 6,
                                                }, children: _jsx(Box, { sx: {
                                                        bgcolor: x.color,
                                                        width: `${totalLearning > 0
                                                            ? Math.min((x.value / totalLearning) * 100, 100)
                                                            : 0}%`,
                                                        height: 6,
                                                        borderRadius: '4px',
                                                        transition: 'width .4s',
                                                    } }) })] }, x.label))) })] }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                border: '1px solid #EAECF0',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                bgcolor: '#fff',
                            }, children: [_jsxs(Box, { sx: {
                                        bgcolor: '#FAFAFA',
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid #EAECF0',
                                    }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "Missed Opportunity" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "Revenue & ROAS of ads stopped before threshold" })] }), _jsx(Box, { sx: { p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }, children: [
                                        {
                                            label: 'Profitable',
                                            desc: 'ROAS ≥ 1x',
                                            count: notableAds.length,
                                            rev: notableAds.reduce((s, a) => s + (a.totalRev || 0), 0),
                                            color: '#027A48',
                                            bg: '#E0EEEB',
                                        },
                                        {
                                            label: 'Generating',
                                            desc: '0 < ROAS < 1x',
                                            count: lowROAS.length,
                                            rev: lowROAS.reduce((s, a) => s + (a.totalRev || 0), 0),
                                            color: '#B08135',
                                            bg: '#F7F0E3',
                                        },
                                    ].map((b) => (_jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.75,
                                        }, children: [_jsxs(Box, { sx: { width: 80, flexShrink: 0 }, children: [_jsx(Typography, { sx: {
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: b.color,
                                                        }, children: b.label }), _jsx(Typography, { sx: {
                                                            fontSize: 10,
                                                            color: '#667085',
                                                            mt: 0.25,
                                                        }, children: b.desc })] }), _jsx(Box, { sx: {
                                                    flex: 1,
                                                    bgcolor: '#F2F4F7',
                                                    borderRadius: '6px',
                                                    height: 28,
                                                    overflow: 'hidden',
                                                }, children: _jsx(Box, { sx: {
                                                        bgcolor: b.bg,
                                                        width: `${totalLearning > 0
                                                            ? Math.min((b.count / totalLearning) * 100, 100)
                                                            : 0}%`,
                                                        height: '100%',
                                                        borderRadius: '6px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        pl: 1.25,
                                                    }, children: b.count > 0 && (_jsxs(Typography, { sx: {
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            color: b.color,
                                                        }, children: [b.count, " ads"] })) }) }), _jsxs(Box, { sx: {
                                                    width: 80,
                                                    textAlign: 'right',
                                                    flexShrink: 0,
                                                }, children: [_jsx(Typography, { sx: {
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: b.color,
                                                        }, children: fK(b.rev) }), _jsx(Typography, { sx: { fontSize: 9, color: '#667085' }, children: "revenue" })] })] }, b.label))) })] }) })] }), _jsxs(Paper, { elevation: 0, sx: {
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    mb: 2.5,
                    overflow: 'hidden',
                    bgcolor: '#fff',
                }, children: [_jsxs(Box, { sx: {
                            bgcolor: '#FAFAFA',
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #EAECF0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "\u26A1 Notable Learning Ads \u2014 Profitable but Stopped" }), _jsxs(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: ["These ads are generating ROAS \u2265 1x but never crossed the $", report.threshold, " threshold \u2014 consider giving them more budget"] })] }), _jsxs(Typography, { sx: { fontSize: 12, color: '#667085', fontWeight: 600 }, children: [notableAds.length, " ads"] })] }), notableAds.length === 0 ? (_jsx(Box, { sx: { p: 3, color: '#667085', fontSize: 12 }, children: "No profitable learning ads found." })) : (_jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: { bgcolor: '#FAFAFA' }, children: [_jsx(TableCell, { sx: thSx(36), children: "#" }), _jsx(TableCell, { sx: thSx(), children: "Ad Name" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "Start" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "Spend" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "Revenue" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "ROAS" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "CT ROAS" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "VT ROAS" })] }) }), _jsx(TableBody, { children: top10Notable.map((ad, i) => (_jsxs(TableRow, { sx: {
                                        bgcolor: i % 2 === 0 ? '#fff' : '#FAFBFC',
                                        '& td': { borderBottom: '1px solid #F2F4F7' },
                                    }, children: [_jsx(TableCell, { sx: { ...tdSx, fontWeight: 700, color: '#EE2A7B' }, children: i + 1 }), _jsxs(TableCell, { sx: { ...tdSx, maxWidth: 380 }, children: [_jsx(Typography, { sx: {
                                                        fontWeight: 600,
                                                        color: '#101828',
                                                        fontSize: '14px !important',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }, title: ad.ad_name, children: ad.ad_name }), _jsx(Typography, { sx: {
                                                        fontSize: 10,
                                                        fontFamily: 'monospace',
                                                        color: '#98A2B3',
                                                        mt: 0.25,
                                                    }, children: ad.ad_id })] }), _jsx(TableCell, { sx: { ...tdSx, textAlign: 'right', color: '#475467' }, children: ad.minDate || '—' }), _jsx(TableCell, { sx: {
                                                ...tdSx,
                                                textAlign: 'right',
                                                fontWeight: 600,
                                                color: '#101828',
                                            }, children: fK(ad.atSpend) }), _jsx(TableCell, { sx: {
                                                ...tdSx,
                                                textAlign: 'right',
                                                fontWeight: 700,
                                                color: '#027A48',
                                            }, children: fK(ad.totalRev) }), _jsx(TableCell, { sx: {
                                                ...tdSx,
                                                textAlign: 'right',
                                                fontWeight: 700,
                                                color: '#027A48',
                                            }, children: fX(ad.computedROAS) }), _jsx(TableCell, { sx: { ...tdSx, textAlign: 'right', color: '#475467' }, children: fX(ad.computedCTROAS) }), _jsx(TableCell, { sx: { ...tdSx, textAlign: 'right', color: '#475467' }, children: fX(ad.computedVTROAS) })] }, ad.ad_id))) })] }))] }), _jsx(Grid, { container: true, spacing: 2.5, children: _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Paper, { elevation: 0, sx: {
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            bgcolor: '#fff',
                        }, children: [_jsxs(Box, { sx: {
                                    bgcolor: '#FAFAFA',
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: '1px solid #EAECF0',
                                }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "Creative / Type Patterns" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "Keyword frequency among profitable learning ads (ROAS \u2265 1x)" }), _jsxs(Box, { sx: {
                                            mt: 1.25,
                                            bgcolor: '#F9FAFB',
                                            border: '1px solid #EAECF0',
                                            borderRadius: '8px',
                                            px: 1.5,
                                            py: 1,
                                        }, children: [_jsx(Typography, { sx: {
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    color: '#475467',
                                                    mb: 0.5,
                                                }, children: "\u2139\uFE0F How to read this table" }), _jsxs(Typography, { sx: {
                                                    fontSize: 10,
                                                    color: '#667085',
                                                    lineHeight: 1.6,
                                                }, children: [_jsx("strong", { style: { color: '#475467' }, children: "Ads column:" }), ' ', "counts how many of the ", notableAds.length, " profitable Learning ads (ROAS \u2265 1x) contain that keyword in their ad name. One ad can match multiple keywords \u2014 e.g. \"Static Video UGC Sale LP\" is counted in all 4 rows.", ' ', _jsxs("strong", { style: { color: '#475467' }, children: ["The column totals will exceed ", notableAds.length, "."] }), ' ', "Note: covers only the ", notableAds.length, " profitable ads, not all ", totalLearning, " Learning ads."] })] })] }), kwC.length === 0 ? (_jsx(Box, { sx: { p: 2.5, color: '#667085', fontSize: 12 }, children: "No keyword matches found in profitable ads." })) : (_jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: { bgcolor: '#FAFAFA' }, children: [_jsx(TableCell, { sx: thSx(), children: "Keyword" }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "Ads" }), _jsxs(TableCell, { sx: thSx(undefined, 'right'), children: ["% of ", notableAds.length, " Profitable"] }), _jsx(TableCell, { sx: thSx(undefined, 'right'), children: "Revenue" })] }) }), _jsx(TableBody, { children: kwC.map(({ kw, count, rev }, i) => (_jsxs(TableRow, { sx: {
                                                bgcolor: i % 2 === 0 ? '#fff' : '#FAFBFC',
                                                '& td': { borderBottom: '1px solid #F2F4F7' },
                                            }, children: [_jsx(TableCell, { sx: tdSx, children: _jsx(Chip, { label: kw, size: "small", sx: {
                                                            bgcolor: '#FCE8F2',
                                                            color: '#EE2A7B',
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            height: 22,
                                                            borderRadius: '5px',
                                                        } }) }), _jsx(TableCell, { sx: {
                                                        ...tdSx,
                                                        textAlign: 'right',
                                                        fontWeight: 600,
                                                        color: '#101828',
                                                    }, children: count }), _jsxs(TableCell, { sx: {
                                                        ...tdSx,
                                                        textAlign: 'right',
                                                        color: '#667085',
                                                    }, children: [notableAds.length
                                                            ? Math.round((count / notableAds.length) * 100)
                                                            : 0, "%"] }), _jsx(TableCell, { sx: {
                                                        ...tdSx,
                                                        textAlign: 'right',
                                                        fontWeight: 700,
                                                        color: '#027A48',
                                                    }, children: fK(rev) })] }, kw))) })] }))] }) }) })] }));
};
// ── Helpers ──────────────────────────────────────────────────────────────
const HeroKPI = ({ label, value, sub, leftColor, valueColor }) => (_jsxs(Paper, { elevation: 0, sx: {
        p: 2.25,
        border: '1px solid #EAECF0',
        borderLeft: `4px solid ${leftColor}`,
        borderRadius: '8px',
        bgcolor: '#fff',
        height: '100%',
    }, children: [_jsx(Typography, { sx: {
                fontSize: 9,
                color: '#98A2B3',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 1,
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: 30,
                fontWeight: 800,
                color: valueColor,
                letterSpacing: '-0.02em',
                lineHeight: 1,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 11, color: '#98A2B3', mt: 1 }, children: sub }))] }));
const thSx = (width, align) => ({
    fontWeight: 700,
    fontSize: 12,
    color: '#667085',
    py: 1,
    px: 1.25,
    width: width,
    textAlign: (align || 'left'),
    borderBottom: '1px solid #EAECF0',
});
const tdSx = {
    py: 1,
    px: 1.25,
    fontSize: 14,
};
export default LearningInsightsTab;
